import { useSearchParams as _useSearchParams, useLocation } from "react-router";
import { parseRawURLSearchParams } from "../url-utils";

const fromSearchParams = <T extends Record<string, string | undefined>>(
  urlParams: URLSearchParams
) => {
  const params: Record<string, string | undefined> = {};
  for (const [key, value] of urlParams) {
    params[key] = value;
  }
  return params as T;
};

const toSearchParams = <T extends Record<string, string | undefined>>(obj: T) => {
  const params = new URLSearchParams();
  for (const key of Object.keys(obj)) {
    if (obj[key] !== undefined) {
      params.append(key, obj[key] as string);
    }
  }
  return params;
};

export const useSearchParams = <T extends Record<string, string | undefined>>(defaultValue?: T) => {
  const [searchParams, setSearchParams] = _useSearchParams();

  const set = (
    setFunction: (prev: Partial<T>) => Partial<T>,
    navigationOptions: { replace?: boolean } = {}
  ) => {
    setSearchParams(
      (prevParams) => toSearchParams(setFunction(fromSearchParams(prevParams))),
      navigationOptions
    );
  };

  return [{ ...defaultValue, ...fromSearchParams<T>(searchParams) }, set] as const;
};

export const useRawSearchParams = <T extends Record<string, string | undefined>>(
  defaultValue?: T
) => {
  const location = useLocation();
  const searchParams = parseRawURLSearchParams(location.search);
  return { ...defaultValue, ...searchParams } as T;
};
